"use client";

import { useEffect, useId, useState } from "react";

import { CalendarIcon, PlusIcon } from "lucide-react";

import { Field, FieldContent, FieldLabel } from "@/components/ui/field";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";

const uiText = {
  cta: {
    createClient: "Créer Client",
  },
  placeholders: {
    birthDate: "jj/mm/aaaa",
  },
  labels: {
    address: "Adresse *",
    birthDate: "Date de Naissance *",
    city: "Ville *",
    email: "Email",
    firstName: "Prénom *",
    lastName: "Nom *",
    phone: "Téléphone",
    postalCode: "Code Postal *",
    siret: "Numéro SIRET",
  },
};

type ClientCreateFormValues = {
  firstName: string;
  lastName: string;
  siret: string;
  birthDate: string;
  addressLine1: string;
  city: string;
  postalCode: string;
  phone: string;
  email: string;
};

const defaultValues: ClientCreateFormValues = {
  firstName: "",
  lastName: "",
  siret: "",
  birthDate: "",
  addressLine1: "",
  city: "",
  postalCode: "",
  phone: "",
  email: "",
};

function ClientCreateForm({
  onCanProceedChange,
  onCreate,
}: {
  onCanProceedChange?: (canProceed: boolean) => void;
  onCreate?: (values: ClientCreateFormValues) => void;
}) {
  const idPrefix = useId();
  const [values, setValues] = useState<ClientCreateFormValues>(defaultValues);

  const canProceed = Boolean(
    values.firstName.trim() &&
    values.lastName.trim() &&
    values.birthDate.trim() &&
    values.addressLine1.trim() &&
    values.city.trim() &&
    values.postalCode.trim(),
  );

  useEffect(() => {
    onCanProceedChange?.(canProceed);
  }, [canProceed, onCanProceedChange]);

  return (
    <div className="space-y-4">
      <Card className="border-muted">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field>
              <FieldLabel htmlFor={`${idPrefix}-firstName`}>
                {uiText.labels.firstName}
              </FieldLabel>
              <FieldContent>
                <Input
                  id={`${idPrefix}-firstName`}
                  required
                  value={values.firstName}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      firstName: e.target.value,
                    }))
                  }
                />
              </FieldContent>
            </Field>
            <Field>
              <FieldLabel htmlFor={`${idPrefix}-lastName`}>
                {uiText.labels.lastName}
              </FieldLabel>
              <FieldContent>
                <Input
                  id={`${idPrefix}-lastName`}
                  required
                  value={values.lastName}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, lastName: e.target.value }))
                  }
                />
              </FieldContent>
            </Field>

            <Field>
              <FieldLabel htmlFor={`${idPrefix}-siret`}>
                {uiText.labels.siret}
              </FieldLabel>
              <FieldContent>
                <Input
                  id={`${idPrefix}-siret`}
                  value={values.siret}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, siret: e.target.value }))
                  }
                />
              </FieldContent>
            </Field>
            <Field>
              <FieldLabel htmlFor={`${idPrefix}-birthDate`}>
                {uiText.labels.birthDate}
              </FieldLabel>
              <FieldContent>
                <InputGroup>
                  <InputGroupInput
                    id={`${idPrefix}-birthDate`}
                    placeholder={uiText.placeholders.birthDate}
                    required
                    value={values.birthDate}
                    onChange={(e) =>
                      setValues((prev) => ({
                        ...prev,
                        birthDate: e.target.value,
                      }))
                    }
                  />
                  <InputGroupAddon align="inline-end">
                    <CalendarIcon className="text-muted-foreground" />
                  </InputGroupAddon>
                </InputGroup>
              </FieldContent>
            </Field>

            <Field className="md:col-span-2">
              <FieldLabel htmlFor={`${idPrefix}-addressLine1`}>
                {uiText.labels.address}
              </FieldLabel>
              <FieldContent>
                <Input
                  id={`${idPrefix}-addressLine1`}
                  required
                  value={values.addressLine1}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      addressLine1: e.target.value,
                    }))
                  }
                />
              </FieldContent>
            </Field>

            <Field>
              <FieldLabel htmlFor={`${idPrefix}-city`}>
                {uiText.labels.city}
              </FieldLabel>
              <FieldContent>
                <Input
                  id={`${idPrefix}-city`}
                  required
                  value={values.city}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, city: e.target.value }))
                  }
                />
              </FieldContent>
            </Field>
            <Field>
              <FieldLabel htmlFor={`${idPrefix}-postalCode`}>
                {uiText.labels.postalCode}
              </FieldLabel>
              <FieldContent>
                <Input
                  id={`${idPrefix}-postalCode`}
                  required
                  value={values.postalCode}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      postalCode: e.target.value,
                    }))
                  }
                />
              </FieldContent>
            </Field>

            <Field>
              <FieldLabel htmlFor={`${idPrefix}-phone`}>
                {uiText.labels.phone}
              </FieldLabel>
              <FieldContent>
                <Input
                  id={`${idPrefix}-phone`}
                  value={values.phone}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, phone: e.target.value }))
                  }
                />
              </FieldContent>
            </Field>
            <Field>
              <FieldLabel htmlFor={`${idPrefix}-email`}>
                {uiText.labels.email}
              </FieldLabel>
              <FieldContent>
                <Input
                  id={`${idPrefix}-email`}
                  type="email"
                  value={values.email}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, email: e.target.value }))
                  }
                />
              </FieldContent>
            </Field>
          </div>

          <div className="mt-6">
            <Button type="button" onClick={() => onCreate?.(values)}>
              <PlusIcon />
              {uiText.cta.createClient}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export type { ClientCreateFormValues };
export { ClientCreateForm };
